import React, { useState } from "react";
import moment from "moment";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";
import { zodResolver } from "@hookform/resolvers/zod";

import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Grid2 as Grid, Tab, Tabs, useTheme } from "@mui/material";

import { Button, Card, Checkbox, FormGroup, Input, Select } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import useTabs from "components/hooks/useTabs";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import ExpectedCargosForm from "view/cargos/components/createEdit/ExpectedCargosForm";
import useCreateOrExistingCharterFormFields, {  charterDefaultValues } from "view/charters/components/form/useCreateOrExistingCharterFormFields";
import { voyageAddDialogSchema, VoyageAddDialogSchemaInput, VoyageAddDialogSchemaOutput } from "view/voyages/utils/voyage.schema";
import { VoyageCallFormTabs } from "view/voyages/utils/VoyageUtils";

import { Fields, getPluginSettingValue, hasOptionalFieldPermission, hasPluginPermission, Language } from "utils";
import { DateUtils } from "utils/DateUtils";

import VoyageCallsTabsForm from "../form/VoyageCallsTabsForm";

import { ChildProps } from "types/commons";
import { CallType } from "__generated__/enums";
import { SaveVoyageDocument, SaveVoyageMutation } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

type VoyageAddDialogProps = {
	open: boolean;
	onClose: () => void;
	onSave: (savedVoyage: ChildProps<SaveVoyageMutation, "voyageCreateOrEdit">) => void;
	ship?: ShipSelectOption;
	customShips?: ShipSelectOption[];
};

const VoyageAddDialog = ({ open, onSave, onClose, ship, customShips }: VoyageAddDialogProps) => {

	const session = useStoreState(state => state.session);
	const ptaInterval = getPluginSettingValue(session, "callPtaIntervalMinutes");
	const ptdDuration = getPluginSettingValue(session, "callPtdDurationMinutes");

	const theme = useTheme();

	const ptaIntervalMinutes = ptaInterval ? parseInt(ptaInterval, 10) : 0;
	const ptdDurationMinutes = ptdDuration ? parseInt(ptdDuration, 10) : 0;

	const defaultPta = ptaIntervalMinutes > 0 ? moment().startOf("minute").valueOf() : null;
	const defaultPtd = defaultPta && ptdDurationMinutes > 0 ? moment(defaultPta).add(ptdDurationMinutes, "minutes").valueOf() : null;

	const defaultValues: VoyageAddDialogSchemaInput = {
		voyageNumber: "",
		ship: ship ?? null,
		isBallast: false,
		calls: [ {
			id: null,
			location: null,
			pta: defaultPta,
			ptd: defaultPtd,
			expectedCargos: [],
		} ],
		charter: charterDefaultValues,
	};

	const [ selectedCallIndex, selectCallIndex ] = useState(0);

	const { control, setValue, handleSubmit, reset, watch, formState } = useForm<VoyageAddDialogSchemaInput, unknown, VoyageAddDialogSchemaOutput>({
		resolver: zodResolver(voyageAddDialogSchema),
		defaultValues: defaultValues,
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "calls",
	});

	const { selectedTab, selectTab } = useTabs<VoyageCallFormTabs>({
		defaultSelected: VoyageCallFormTabs.Details,
		disableParams: true,
	});

	const [ saveVoyage, { loading } ] = useMutation(SaveVoyageDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
		selectCallIndex(0);
		selectTab(VoyageCallFormTabs.Details);
	};

	const handleOnDelete = (index: number) => {
		remove(index);

		if (selectedCallIndex !== 0) {
			selectCallIndex(selectedCallIndex - 1);
		}
	};

	const save = async (data: VoyageAddDialogSchemaOutput) => {

		const voyageResult = await saveVoyage({
			variables: {
				input: {
					voyageNumber: data.voyageNumber || null,
					shipId: data.ship?.id,
					creditor: charterInput.creditor ? {
						id: charterInput.creditor?.id,
					} : undefined,
					isBallast: data.isBallast,
					calls: data.calls.map(call => ({
						locationId: call.location?.id,
						pta: call.pta,
						ptd: call.ptd,
						expectedCargos: data.isBallast ? [] : call.expectedCargos?.map(expectedCargo => ({
							id: expectedCargo.id,
							callId: call.id,
							actionType: expectedCargo.actionType,
							amount: expectedCargo.amount ?? 0,
							dischargingLocationId: expectedCargo.actionType === CallType.Loading ? expectedCargo.dischargingLocation?.id : null,
							containerTypeCode: expectedCargo.containerType?.isoTypeCode ?? "",
							full: expectedCargo.full,
						})),
					})),
					purchaseCharters: [ charterInput ],
				},
			},
		});

		if (voyageResult.data) {
			onSave(voyageResult.data.voyageCreateOrEdit);
			handleOnClose();
		}
	};

	const values = watch();
	const hasExpectedCargos = values.calls.some(call => call.expectedCargos.length > 0);

	const { charterInput, charterFields } = useCreateOrExistingCharterFormFields({
		control: control,
		setValue: setValue,
		watch: watch,
		formState: formState,
		ship: values.ship ?? ship,
		voyage: undefined,
		cargos: undefined,
		createContractDisabled: true,
	});

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CREATE_VOYAGE" })}
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			form="add-voyage-form"
			loading={loading}
			maxWidth="xl">
			<form onSubmit={handleSubmit(save)} id="add-voyage-form">
				<Grid container spacing={3}>
					<Grid size={4}>
						<Card>
							<FormGroup
								label={Language("VOYAGE_NUMBER")}
								labelPlacement="left-center"
								tooltip={intl.formatMessage({ id: "VOYAGE_NUMBER_TIPS" })}
								overflow="hidden">
								<Controller
									name="voyageNumber"
									control={control}
									render={({ field }) => <Input {...field} value={field.value ?? undefined} />} />
							</FormGroup>

							<FormGroup
								label={Language("SHIP")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name="ship"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											{customShips ? (
												<Select {...field} fetchOptions={customShips} disabled={!!ship} />
											) : (
												<ShipSelect {...field} disabled={!!ship} />
											)}
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							{hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast") && (
								<FormGroup
									label={Language("IS_BALLAST")}
									tooltip={hasExpectedCargos ? intl.formatMessage({ id: "BALLAST_TRIP_CANNOT_HAVE_EXPECTED_CARGOS" }) : undefined}
									labelPlacement="left-center"
									overflow="hidden">
									<Controller
										name="isBallast"
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												onChange={event => {
													field.onChange(event.target.checked);

													if (event.target.checked) {
														selectTab(VoyageCallFormTabs.Details);
													}
												}}
												checked={field.value}
												disabled={hasExpectedCargos} />
										)} />
								</FormGroup>
							)}

							<h4 style={{ fontSize: 16, fontWeight: 600, margin: "24px 0 16px" }}>
								{intl.formatMessage({ id: "CHARTER" })}
							</h4>

							{charterFields}
						</Card>
					</Grid>

					<Grid size={8}>
						<>
							{values.calls.length > 0 && (
								<VoyageCallsTabsForm
									calls={values.calls}
									selectedIndex={selectedCallIndex}
									onSelect={(index) => selectCallIndex(index)}
									errors={formState.errors} />
							)}

							<Box display="flex" justifyContent="end" padding="4px 0 16px">
								<Button
									onClick={() => {

										const lastCall = values.calls.at(-1);
										const pta = ptaIntervalMinutes > 0 ? (lastCall?.ptd ? moment(lastCall.ptd).add(ptaIntervalMinutes, "minutes").valueOf() : moment().startOf("minute").valueOf()) : null;
										const ptd = pta && ptdDurationMinutes > 0 ? moment(pta).add(ptdDurationMinutes, "minutes").valueOf() : null;

										append({
											id: null,
											location: null,
											pta: pta,
											ptd: ptd,
											expectedCargos: [],
										});
									}}
									variant="outlined"
									size="small"
								>
									{Language("CALL_ADD")}
								</Button>
							</Box>

							{fields.length > 0 && (
								<div className="voyage-calls-create-edit-block">
									<Card
										title={(
											<Tabs
												variant='scrollable'
												value={selectedTab}
												onChange={(_event, newValue) => selectTab(newValue)}
											>
												<Tab label={Language("DETAILS")} value={VoyageCallFormTabs.Details} />

												{hasPluginPermission(session, "useContainers") &&
													hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos")
													&& !values.isBallast && (
													<Tab label={Language("EXPECTED_CONTAINERS")} value={VoyageCallFormTabs.Expected} />
												)
												}
											</Tabs>
										)}
										actions={[ {
											icon: <DeleteIcon />,
											onClick: () => handleOnDelete(selectedCallIndex),
											disabled: fields.length < 2,
										} ]}
										disablePadding>
										<React.Fragment>
											{selectedTab === VoyageCallFormTabs.Details  && (
												<Grid
													key={selectedCallIndex}
													container
													spacing={3}
													padding={1.5}>
													<Grid size={12}>
														<FormGroup label={Language("LOCATION")} overflow="hidden" required>
															<Controller
																name={`calls.${selectedCallIndex}.location`}
																control={control}
																render={({ field, formState }) => (
																	<React.Fragment>
																		<LocationSelect {...field} clearable />
																		<FormErrorMessage name={field.name} errors={formState.errors} />
																	</React.Fragment>
																)} />
														</FormGroup>
													</Grid>

													<Grid size={6}>
														<FormGroup label={Language("CALLS_PTA")} overflow="hidden" required>
															<Controller
																name={`calls.${selectedCallIndex}.pta`}
																control={control}
																render={({ field, formState }) => (
																	<React.Fragment>
																		<DatePicker
																			{...field}
																			minDate={DateUtils.getDateOrUndefined(charterInput.validFrom)}
																			showTimeInput />
																		<FormErrorMessage name={field.name} errors={formState.errors} />
																	</React.Fragment>
																)} />
														</FormGroup>
													</Grid>

													<Grid size={6}>
														<FormGroup label={Language("CALLS_PTD")} overflow="hidden" required>
															<Controller
																name={`calls.${selectedCallIndex}.ptd`}
																control={control}
																render={({ field, formState }) => (
																	<React.Fragment>
																		<DatePicker
																			{...field}
																			maxDate={DateUtils.getDateOrUndefined(charterInput.validUntil)}
																			showTimeInput />
																		<FormErrorMessage name={field.name} errors={formState.errors} />
																	</React.Fragment>
																)} />
														</FormGroup>
													</Grid>
												</Grid>
											)}

											{hasPluginPermission(session, "useContainers") &&
												hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos") &&
												!values.isBallast && selectedTab === VoyageCallFormTabs.Expected && (
												<Grid size={12}>
													<ExpectedCargosForm
														key={selectedCallIndex}
														control={control}
														formState={formState}
														nameBase={`calls.${selectedCallIndex}.expectedCargos`}
														expectedCargos={values.calls[selectedCallIndex]?.expectedCargos ?? []}
													/>
												</Grid>
											)}
										</React.Fragment>
									</Card>
								</div>
							)}
						</>
					</Grid>
				</Grid>
			</form>
		</FormDialog>
	);
};

export default VoyageAddDialog;
